import {
  SliderModal,
  Button,
  Field,
  CheckboxGroup,
  Checkbox,
  Input
} from '@see-ui/core';
import { useEffect, useState } from 'react';
import * as service from '@services/api/member-pharmacy-service';
import { useGlobalStore } from '@services/store/global-store';

interface Props {
  onClose: any;
  commPreferences: any[];
  notificationMap: any;
  onPreferencesUpdated?: () => void;
}
const UpdateSettings: React.FC<Props> = ({
  onClose,
  commPreferences,
  notificationMap,
  onPreferencesUpdated
}) => {
  const [updatedNotificationMap, setUpdatedNotificationMap] =
    useState(notificationMap);
  const [contactDetails, setContactDetails] = useState<any>({});
  const { memberDetails } = useGlobalStore((state) => state);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<any>({});

  const handleEdit = (field: string) => {
    setEditingField(field);
    setEditValues({ ...editValues, [field]: contactDetails[field] });
  };

  const handleEditChange = (field: string, value: string) => {
    setEditValues({ ...editValues, [field]: value });
  };

  const handleEditSave = async (field: string) => {
    const updatedValue = editValues[field];
    setContactDetails({ ...contactDetails, [field]: updatedValue });
    setEditingField(null);
    const req = { [field]: updatedValue };
    const request = {
      hcid: memberDetails?.member?.memberPolicy?.hcid,
      ...req
    };
    const response = await service.updateContactDetails(request);
    if (response?.data?.message === 'SUCCESS') {
      console.log(`${field} updated successfully.`);
    }
  };

  const handleEditCancel = () => {
    setEditingField(null);
  };
  useEffect(() => {
    const fetchContactDetails = async () => {
      try {
        const req = {
          hcid: memberDetails?.member?.memberPolicy?.hcid
        };
        const response = await service.getContactDetails(req);

        if (response?.data?.patientContactDetails) {
          setContactDetails(response?.data?.patientContactDetails);
        }
      } catch (error) {
        console.error('Error fetching contact details:', error);
      }
    };

    fetchContactDetails();
  }, []);

  const handleCheckboxChange = (
    category: string,
    type: string,
    isChecked: boolean
  ) => {
    setUpdatedNotificationMap((prev) => {
      const updatedCategory = isChecked
        ? [...(prev[category] || []), type]
        : prev[category]?.filter((item: string) => item !== type);
      return {
        ...prev,
        [category]: updatedCategory
      };
    });
  };

  const onSubmit = async () => {
    const commPreferences: any[] = [];

    const textNotifications: string[] = [];
    if (updatedNotificationMap?.OS?.includes('Text'))
      textNotifications.push('OS');
    if (updatedNotificationMap?.RR?.includes('Text'))
      textNotifications.push('RR');
    if (textNotifications.length > 0 && contactDetails.text) {
      commPreferences.push({
        communicationChannel: contactDetails.text,
        communicationChannelTypeCode: 'Text',
        communicationChannelTypeDescription: 'Text',
        notifications: textNotifications
      });
    }

    const emailNotifications: any[] = [];
    if (updatedNotificationMap?.OS?.includes('Email'))
      emailNotifications.push('OS');
    if (updatedNotificationMap?.RR?.includes('Email'))
      emailNotifications.push('RR');
    if (emailNotifications.length > 0 && contactDetails.email) {
      commPreferences.push({
        communicationChannel: contactDetails.email,
        communicationChannelTypeCode: 'Email',
        communicationChannelTypeDescription: 'Email',
        notifications: emailNotifications
      });
    }


    const payload = {
      hcid: memberDetails?.member?.memberPolicy?.hcid,
      termsAndConditionsAccepted: true,
      commPreferences
    };
    const response: any = await service.saveCommunicationPreferences(payload);
    if (response?.message === 'SUCCESS') {
      console.log('Communication preferences updated successfully.');
      if (onPreferencesUpdated) {
        onPreferencesUpdated();
      }

      onClose();
    }
  };

  return (
    <>
      <SliderModal
        direction="right"
        labelAriaCloseBtn="Close Modal"
        onClose={onClose}
        open
      >
        <div className="px-10">
          <SliderModal.Body>
            <h2>CarelonRx Pharmacy Communication Settings</h2>
            <p className="pt-5">
              Let us know how to contact you for updates about your home
              delivery prescriptions, including refills and order statuses.
            </p>
            <div className="pt-10">
              <div className="grid grid-cols-3 md:grid-cols-3 gap-4 ">
                <div>
                  <span className="font-semibold">Phone</span>
                </div>
                <div>
                  {editingField === 'phone' ? (
                    <Input
                      type="text"
                      value={editValues.phone || ''}
                      onChange={(e) =>
                        handleEditChange('phone', e.target.value)
                      }
                    />
                  ) : contactDetails.phone ? (
                    contactDetails.phone.replace(
                      /(\d{3})(\d{3})(\d{4})/,
                      '$1-$2-$3'
                    )
                  ) : null}
                </div>
                <div>
                  {editingField === 'phone' ? (
                    <>
                      <Button
                        variant="link"
                        onClick={() => handleEditSave('phone')}
                      >
                        Save
                      </Button>
                      <Button variant="link" onClick={handleEditCancel}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button variant="link" onClick={() => handleEdit('phone')}>
                      Edit
                    </Button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 md:grid-cols-3 gap-4 ">
                <div>
                  <span className="font-semibold">Text</span>
                </div>

                {editingField === 'text' ? (
                  <Input
                    type="text"
                    value={editValues.text || ''}
                    onChange={(e) => handleEditChange('text', e.target.value)}
                  />
                ) : contactDetails.text ? (
                  contactDetails.text.replace(
                    /(\d{3})(\d{3})(\d{4})/,
                    '$1-$2-$3'
                  )
                ) : null}
                <div>
                  {editingField === 'text' ? (
                    <>
                      <Button
                        variant="link"
                        onClick={() => handleEditSave('text')}
                      >
                        Save
                      </Button>
                      <Button variant="link" onClick={handleEditCancel}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button variant="link" onClick={() => handleEdit('text')}>
                      Edit
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 md:grid-cols-3 gap-4 ">
                <div>
                  <span className="font-semibold">Email</span>
                </div>
                <div>
                  {editingField === 'email' ? (
                    <Input
                      type="email"
                      value={editValues.email || ''}
                      onChange={(e) =>
                        handleEditChange('email', e.target.value)
                      }
                    />
                  ) : contactDetails.email ? (
                    contactDetails.email
                  ) : null}
                </div>
                <div>
                  {editingField === 'email' ? (
                    <>
                      <Button
                        variant="link"
                        onClick={() => handleEditSave('email')}
                      >
                        Save
                      </Button>
                      <Button variant="link" onClick={handleEditCancel}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button variant="link" onClick={() => handleEdit('email')}>
                      Edit
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div>
              <h4> Order Status</h4>
              <p className="pb-4 pt-1">
                Your prescriptions(s) order free delivery is on the way! Track
                your order every step of the way.
              </p>
              <div className="flex items-center space-x-4">
                <Checkbox
                  label="Text"
                  name="text"
                  checked={updatedNotificationMap?.OS?.includes('Text')}
                  onCheckedChange={(isChecked) =>
                    handleCheckboxChange('OS', 'Text', isChecked ? true : false)
                  }
                />
                <Checkbox
                  label="Email"
                  name="email"
                  checked={updatedNotificationMap?.OS?.includes('Email')}
                  onCheckedChange={(isChecked) =>
                    handleCheckboxChange(
                      'OS',
                      'Email',
                      isChecked ? true : false
                    )
                  }
                />
              </div>
            </div>

            <div className="pt-8 pb-3">
              <h4> Refill Reminder</h4>
              <p className="pb-4 pt-1">
                Don’t forget to refills! Let us know the best way to remind you
                that you're due for a refill.
              </p>
              <div className="flex items-center space-x-4">
                <Checkbox
                  label="Text"
                  name="text"
                  checked={updatedNotificationMap?.RR?.includes('Text')}
                  onCheckedChange={(isChecked) =>
                    handleCheckboxChange('RR', 'Text', isChecked ? true : false)
                  }
                />
                <Checkbox
                  label="Email"
                  name="email"
                  checked={updatedNotificationMap?.RR?.includes('Email')}
                  onCheckedChange={(isChecked) =>
                    handleCheckboxChange(
                      'RR',
                      'Email',
                      isChecked ? true : false
                    )
                  }
                />
              </div>
            </div>
          </SliderModal.Body>
        </div>
        <SliderModal.Footer>
          <Button variant="primary" onClick={onSubmit}>
            Save
          </Button>
          <Button className="ml-4" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </SliderModal.Footer>
      </SliderModal>
    </>
  );
};

export default UpdateSettings;
